/**
 * INTERPOLATION AND PLURALS — the two capabilities the schema work deliberately did not build.
 * ===========================================================================================
 *
 * `src/i18n/` shipped without a message formatter on the grounds that no schema string needed one. The
 * strings that *block submission* are the counter-example: `validate.ts` cannot say "Must be at least 8
 * characters" without a number in the sentence, and it cannot say it grammatically in six languages
 * without plural categories.
 *
 * ⚠️ **CORRECTING THE CLAIM THAT JUSTIFIED SHIPPING WITHOUT THIS FILE.** `src/i18n/` asserted that "all
 * 648 schema strings contain zero placeholders". That was **1-of-648 wrong**: a sibling workstream found
 * `{intermediateAnchorCount}` in `maintenance.photo_intermediate_anchor.help`, reaching a technician
 * verbatim, because nothing substituted it — `FieldControl` renders `field.help` as-is and `Translator`
 * has no interpolation. The help text has since been reworded ("A five-column locker has four joins."),
 * so the count is genuinely zero **today**, and `test/forms/schema-defects.test.ts` holds it there.
 *
 * The claim is restated rather than deleted because the near-miss is the reason this file exports
 * {@link PLACEHOLDER_SOURCE} instead of hiding its regex: the original was not a stable property of the
 * schemas, it was an unenforced observation about them, and it was already false when it was written. If
 * a schema string ever legitimately takes a parameter it must come through `formatMessage` here, not
 * through a second substitution helper that could disagree about the token shape.
 *
 * ⚠️ **THE DEPENDENCY DECISION, ALREADY MADE: NO ICU LIBRARY.** A full ICU message formatter is
 * ~40 kB gzipped against an entry bundle already at ~113 kB gzipped plus a 1.09 MB wasm decoder, for
 * an app used in basements. `Intl.PluralRules` is part of the platform, costs zero bytes, and carries
 * the same CLDR plural data the library would ship a copy of. What is deliberately NOT supported is
 * nested select/plural and ordinal-with-gender; if a message ever genuinely needs those, that is the
 * point to escalate rather than the point to quietly add a dependency.
 *
 * ─── WHY NOT `n === 1 ? singular : plural`, MEASURED ────────────────────────────────────────────
 *
 * The ternary is not a shortcut, it is wrong, and it is wrong in a phase-1 locale. Measured with
 * Node 22 / ICU 77:
 *
 *   | count     | en-GB | de-DE | es-ES | it-IT | fr-FR | ja-JP |
 *   |-----------|-------|-------|-------|-------|-------|-------|
 *   | 0         | other | other | other | other | **one** | other |
 *   | 1         | one   | one   | one   | one   | one   | other |
 *   | 2         | other | other | other | other | other | other |
 *   | 1 000 000 | other | other | **many** | **many** | **many** | other |
 *   | 1.5       | other | other | other | other | **one** | other |
 *
 * French puts **zero** in the `one` category ("0 photo", not "0 photos"), and es/it/fr have a `many`
 * category the ternary cannot reach at all. Japanese has exactly one category, so a ternary forces a
 * translator to write the same sentence twice and invites them to differ by accident.
 *
 * ─── A NUMBER IS GROUPED ONLY IF THE TECHNICIAN DOES NOT HAVE TO TYPE IT BACK ───────────────────
 *
 * The two kinds of number in a blocking message look identical and must be formatted differently, so
 * the rule is carried by the **parameter name** and enforced by {@link GROUPED_PARAMS}:
 *
 *   | parameter       | example message                     | formatting          | why |
 *   |-----------------|-------------------------------------|---------------------|-----|
 *   | `{count}`       | `Must be at most 20000 characters`  | `Intl.NumberFormat` | a quantity that is only ever READ |
 *   | `{min}` `{max}` | `Must be at most 3000`              | digits as authored  | a bound the technician TYPES BACK |
 *
 * `{count}` is a character count, a photo count or a selection count. Nobody types it, so a German
 * technician should read `20.000` rather than `20000`, and `Intl.NumberFormat` is the platform's own
 * CLDR grouping data at zero bytes.
 *
 * `{min}`/`{max}` are the opposite case and **must not** be grouped. `src/i18n/format.ts` already
 * establishes that display and payload are different functions; a numeric bound sits on the payload
 * side, because *"Must be at most 3.000"* under de-DE invites a technician to type `3.000` into a field
 * where `Number("3.000")` is `3`. Grouping there converts a readability nicety into a wrong submission.
 *
 * That distinction is the whole reason this is not one blanket policy. `format.ts`'s `displayNumber`
 * remains the right tool for a number that is only ever read outside a message.
 *
 * ─── LISTS JOIN WITH `Intl.ListFormat`, BUT THEIR ITEMS ARE NEVER TRANSLATED ─────────────────────
 *
 * `Not an option: {values}` interpolates one or more Salesforce picklist **values**. The values are
 * submitted verbatim and several land on restricted picklists, so they are never translated — but the
 * *connective between them* is prose, and `", "` is an English-language choice. {@link formatList}
 * uses `Intl.ListFormat` so de reads `Alpha und Beta`, while each item passes through byte-identical.
 *
 * `type: 'conjunction'` ("and", not "or") is correct here: every listed value is invalid, so the
 * sentence asserts something true of all of them rather than a choice between them.
 */

/** The CLDR plural categories. Which of these a locale actually uses is `pluralCategoriesFor`. */
export type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';

/**
 * Canonical ordering. Used for hashing and for the translator handoff, so that two authors writing
 * the same forms in a different key order produce the same hash and not a phantom staleness.
 */
export const PLURAL_CATEGORY_ORDER: readonly PluralCategory[] = ['zero', 'one', 'two', 'few', 'many', 'other'];

/**
 * A message that varies by count.
 *
 * `other` is **required**, and is the only required category, because it is the one category every
 * locale has. That makes "a plural message with nothing to render" unrepresentable rather than a
 * runtime blank — the requirement that a blocking message is never empty is carried by the type.
 */
export interface PluralForms {
  readonly other: string;
  readonly zero?: string;
  readonly one?: string;
  readonly two?: string;
  readonly few?: string;
  readonly many?: string;
}

/** Either a plain string or a set of plural forms. Schema strings are always the former. */
export type MessagePattern = string | PluralForms;

/** Values substituted into `{token}` placeholders. */
export type MessageParams = Readonly<Record<string, string | number>>;

/** The one parameter name `Intl.PluralRules` is asked about. Fixed, so a message cannot mis-name it. */
export const PLURAL_COUNT_PARAM = 'count';

/**
 * The placeholder shape, as a source string.
 *
 * Exported as source rather than as a `RegExp` because a `/g` regex carries `lastIndex` state, and a
 * module-level shared one produces the classic "every other call fails" bug. Callers get a fresh
 * regex from {@link placeholderPattern}.
 *
 * ⚠️ Shape coordination: the schema workstream found `{intermediateAnchorCount}` rendering literally
 * in maintenance help text, so `{name}` is the shape both sides must agree on. This is the single
 * definition of it.
 */
export const PLACEHOLDER_SOURCE = '\\{([A-Za-z][A-Za-z0-9_]*)\\}';

/** A fresh global regex over {@link PLACEHOLDER_SOURCE}. Never share one. */
export function placeholderPattern(): RegExp {
  return new RegExp(PLACEHOLDER_SOURCE, 'g');
}

export function isPluralPattern(pattern: MessagePattern): pattern is PluralForms {
  return typeof pattern === 'object' && pattern !== null && typeof pattern.other === 'string';
}

/** Every form string in a pattern, in canonical category order. One entry for a plain string. */
export function patternForms(pattern: MessagePattern): readonly string[] {
  if (!isPluralPattern(pattern)) return [pattern];
  const out: string[] = [];
  for (const category of PLURAL_CATEGORY_ORDER) {
    const form = pattern[category];
    if (form !== undefined) out.push(form);
  }
  return out;
}

/** The categories a pattern actually declares, in canonical order. `[]` for a plain string. */
export function declaredCategories(pattern: MessagePattern): readonly PluralCategory[] {
  if (!isPluralPattern(pattern)) return [];
  return PLURAL_CATEGORY_ORDER.filter((category) => pattern[category] !== undefined);
}

/**
 * The single string a pattern is hashed against.
 *
 * For a plain string it is the string itself, so schema entries keep the hashes they already have and
 * nothing in the existing catalogue is invalidated by this file existing. For a plural it is every
 * declared form, labelled and in canonical order — so **editing any one form invalidates the whole
 * entry**. That is deliberate: a translator translated the set together, and a reworded `other` with
 * a stale `one` is the same hazard as a stale sentence.
 */
export function canonicalSource(pattern: MessagePattern): string {
  if (!isPluralPattern(pattern)) return pattern;
  return declaredCategories(pattern)
    .map((category) => `${category}: ${pattern[category] ?? ''}`)
    .join('\n');
}

/** Placeholder names used anywhere in a pattern, de-duplicated, in first-seen order. */
export function placeholdersIn(pattern: MessagePattern): readonly string[] {
  const seen: string[] = [];
  for (const form of patternForms(pattern)) {
    for (const match of form.matchAll(placeholderPattern())) {
      const name = match[1];
      if (name !== undefined && !seen.includes(name)) seen.push(name);
    }
  }
  return seen;
}

/* ------------------------------------------------------------------------- plural rules ---- */

const pluralRulesCache = new Map<string, Intl.PluralRules>();

/**
 * `Intl.PluralRules` for a locale, cached.
 *
 * Construction is one of the more expensive things in the platform and a form with 30 problems on the
 * review screen would otherwise construct 30 of them.
 *
 * A runtime with trimmed CLDR data can throw or fall back on an unknown tag; the `catch` degrades to
 * the source locale's rules rather than to a crash, because a blocking validation message that throws
 * leaves the technician with a form that will not submit and no message at all.
 */
function pluralRules(locale: string): Intl.PluralRules {
  const hit = pluralRulesCache.get(locale);
  if (hit !== undefined) return hit;
  let made: Intl.PluralRules;
  try {
    made = new Intl.PluralRules(locale);
  } catch {
    made = new Intl.PluralRules('en-GB');
  }
  pluralRulesCache.set(locale, made);
  return made;
}

/** The plural category `count` falls in for `locale`. */
export function pluralCategory(locale: string, count: number): PluralCategory {
  return pluralRules(locale).select(count) as PluralCategory;
}

/** The categories a locale uses at all, in canonical order. `ja-JP` → `['other']`. */
export function pluralCategoriesFor(locale: string): readonly PluralCategory[] {
  const used = new Set(pluralRules(locale).resolvedOptions().pluralCategories as PluralCategory[]);
  return PLURAL_CATEGORY_ORDER.filter((category) => used.has(category));
}

/* ------------------------------------------------------------- numbers and lists ---------- */

/**
 * Parameter names whose value is a **quantity to read** and is therefore locale-formatted.
 *
 * Everything not named here keeps its digits exactly as authored. See the header table: `{count}` is a
 * character/photo/selection count nobody types, while `{min}`/`{max}` are bounds a technician types
 * back into the field, where a grouping separator produces a wrong value rather than a nicer sentence.
 *
 * Tied to {@link PLURAL_COUNT_PARAM} rather than restating `'count'`, so the parameter that drives
 * plural selection and the parameter that gets grouped cannot drift apart.
 */
export const GROUPED_PARAMS: readonly string[] = [PLURAL_COUNT_PARAM];

const numberFormatCache = new Map<string, Intl.NumberFormat>();
const listFormatCache = new Map<string, Intl.ListFormat>();

/**
 * A locale-grouped rendering of a quantity.
 *
 * The `catch` degrades to `String(n)` for the same reason `pluralRules` degrades: a blocking validation
 * message that throws leaves the technician with a form that will not submit and no message at all, and
 * ungrouped digits are a cosmetic loss next to that.
 */
export function formatCount(locale: string, n: number): string {
  let formatter = numberFormatCache.get(locale);
  if (formatter === undefined) {
    try {
      formatter = new Intl.NumberFormat(locale);
    } catch {
      formatter = new Intl.NumberFormat('en-GB');
    }
    numberFormatCache.set(locale, formatter);
  }
  try {
    return formatter.format(n);
  } catch {
    return String(n);
  }
}

/**
 * Join items with the locale's own list connective, leaving every item byte-identical.
 *
 * ⚠️ **The items are data, not prose.** Callers pass Salesforce picklist values; only the punctuation
 * and conjunction between them is localised. See the header.
 *
 * `Intl.ListFormat` is ES2021 and absent from a few older mobile browsers this app has to run on, so a
 * missing constructor falls back to `", "` — the wording this replaced — rather than throwing inside a
 * message that blocks submission.
 */
export function formatList(locale: string, items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0] ?? '';
  if (typeof Intl.ListFormat !== 'function') return items.join(', ');
  let formatter = listFormatCache.get(locale);
  if (formatter === undefined) {
    try {
      formatter = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });
    } catch {
      formatter = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' });
    }
    listFormatCache.set(locale, formatter);
  }
  try {
    return formatter.format([...items]);
  } catch {
    return items.join(', ');
  }
}

/** Test seam: `Intl` construction is cached and a test that walks locales wants a clean slate. */
export function resetPluralRulesCache(): void {
  pluralRulesCache.clear();
  numberFormatCache.clear();
  listFormatCache.clear();
}

export interface SelectedForm {
  readonly text: string;
  /** The category `count` fell in for this locale. */
  readonly category: PluralCategory;
  /** `true` when the pattern had no form for `category` and `other` was used instead. */
  readonly usedOther: boolean;
}

/**
 * Choose a form for `count`.
 *
 * Falling back to `other` when a category is absent is correct, not lax: a Japanese translation
 * legitimately supplies only `other`, and an English source legitimately supplies only `one` and
 * `other` while `es-ES` has a `many` its translator may or may not differentiate. `usedOther` records
 * that it happened so a coverage report can show it rather than it being invisible.
 */
export function selectPluralForm(forms: PluralForms, locale: string, count: number): SelectedForm {
  const category = pluralCategory(locale, count);
  const exact = forms[category];
  if (exact !== undefined) return { text: exact, category, usedOther: false };
  return { text: forms.other, category, usedOther: true };
}

/* -------------------------------------------------------------------------- formatting ---- */

export interface Formatted {
  /** Never empty for a well-formed pattern, and never a bare key — that is `resolve.ts`'s job. */
  readonly text: string;
  /**
   * Placeholder names that had no parameter. **The token is left in the output verbatim**, so an
   * unsubstituted `{count}` is visibly a bug rather than a silently mangled sentence.
   *
   * Deleting the token instead would turn *"Must be at least 8 characters"* into *"Must be at least
   * characters"* — fluent, plausible and meaningless, which is this project's recurring defect. A test
   * asserts this array is empty for every message the app actually renders.
   */
  readonly unresolved: readonly string[];
  /** Present only for a plural pattern. */
  readonly category?: PluralCategory;
  /** `true` when a plural pattern had to fall back to its `other` form. */
  readonly usedOther?: boolean;
}

/**
 * Substitute `{token}`s. Unknown tokens are left in place and reported.
 *
 * `locale` is optional and omitting it means **no number is locale-formatted** — the digits of every
 * parameter go in exactly as authored. That default is deliberate: an un-localed call cannot silently
 * pick a grouping convention, so the only way to get `20.000` is to say which locale asked for it.
 * `formatMessage` always passes one.
 */
export function interpolate(template: string, params: MessageParams = {}, locale?: string): Formatted {
  const unresolved: string[] = [];
  const text = template.replace(placeholderPattern(), (whole, name: string) => {
    const value = params[name];
    if (value === undefined) {
      if (!unresolved.includes(name)) unresolved.push(name);
      return whole;
    }
    /*
     * A quantity to READ is grouped; a bound to TYPE BACK is not. See the header table — this one
     * `if` is where "3000" stays "3000" for `{max}` and becomes "3.000" for `{count}` under de-DE.
     */
    if (locale !== undefined && typeof value === 'number' && GROUPED_PARAMS.includes(name)) {
      return formatCount(locale, value);
    }
    return String(value);
  });
  return { text, unresolved };
}

/**
 * Render a pattern for a locale.
 *
 * The order is: select the plural form for `params.count`, *then* interpolate. Doing it the other way
 * round would mean the count had already been stringified before `Intl.PluralRules` saw it.
 */
export function formatMessage(
  pattern: MessagePattern,
  locale: string,
  params: MessageParams = {},
): Formatted {
  if (!isPluralPattern(pattern)) return interpolate(pattern, params, locale);

  const raw = params[PLURAL_COUNT_PARAM];
  const count = typeof raw === 'number' ? raw : Number.NaN;
  if (!Number.isFinite(count)) {
    /*
     * A plural pattern rendered without a count. `other` is the safest form to show, but the missing
     * parameter is reported whether or not the form happens to contain a `{count}` token — otherwise a
     * plural message whose `other` form omits the number would hide the mistake completely.
     */
    const fallback = interpolate(pattern.other, params, locale);
    const unresolved = fallback.unresolved.includes(PLURAL_COUNT_PARAM)
      ? fallback.unresolved
      : [...fallback.unresolved, PLURAL_COUNT_PARAM];
    return { text: fallback.text, unresolved, category: 'other', usedOther: true };
  }

  const selected = selectPluralForm(pattern, locale, count);
  // Plural category is chosen from the RAW number above, before grouping turns it into a string.
  const filled = interpolate(selected.text, params, locale);
  return {
    text: filled.text,
    unresolved: filled.unresolved,
    category: selected.category,
    usedOther: selected.usedOther,
  };
}
